import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { CrudStyles } from '../../shared/Crud';
import { formatCurrencyBR, parseCurrencyCentsFromInput } from '../../../utils/masks';
import deliveryService from '../../../services/deliveryService';
import ConfirmRemoveItemModal from '../ConfirmRemoveItemModal';
import MessageModal from '../../shared/MessageModal';

const { FormGroup, Input, FormRow, SectionTitle, Button, RemoveButton, CheckboxGroup } = CrudStyles;

const VISIBLE_ITEMS_WITHOUT_SCROLL = 3;

const ItemsListScroll = styled.div`
    ${({ $maxHeight }) =>
        $maxHeight != null
            ? `
        max-height: ${$maxHeight}px;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        padding-right: 4px;
        margin-bottom: 8px;
    `
            : ''}
`;

const ItemCard = styled.div`
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
    background: #fafafa;
`;

const emptyItem = () => ({
    id: `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    quantity: '',
    height: '',
    width: '',
    length: '',
    weight: '',
    fragile: false,
    price: null,
    documentNumber: '',
    description: '',
});

export default function ItemsStep({
    items = [],
    onChange,
    errors = {},
    deliveryId,
    customerIdForApi,
}) {
    const [localErrors, setLocalErrors] = useState({});
    const [savingIndex, setSavingIndex] = useState(null);
    const [removingIndex, setRemovingIndex] = useState(null);
    const [confirmRemoveIndex, setConfirmRemoveIndex] = useState(null);
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });
    const [scrollMaxHeight, setScrollMaxHeight] = useState(null);
    const listScrollRef = useRef(null);

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    // Garante pelo menos um item em edição quando a lista fica vazia (pai usa onChange inline)
    useEffect(() => {
        if (items?.length) return;
        onChangeRef.current([emptyItem()]);
    }, [items?.length]);

    const list = items?.length ? items : [];

    useLayoutEffect(() => {
        const root = listScrollRef.current;
        if (!root) return undefined;

        const measure = () => {
            const cards = root.querySelectorAll('[data-delivery-item-card]');
            if (cards.length <= VISIBLE_ITEMS_WITHOUT_SCROLL) {
                setScrollMaxHeight(null);
                return;
            }

            let total = 0;
            for (let i = 0; i < VISIBLE_ITEMS_WITHOUT_SCROLL; i += 1) {
                const card = cards[i];
                const style = window.getComputedStyle(card);
                const marginBottom = parseFloat(style.marginBottom) || 0;
                total += card.offsetHeight + marginBottom;
            }
            setScrollMaxHeight(total);
        };

        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(root);
        root.querySelectorAll('[data-delivery-item-card]').forEach((card) => observer.observe(card));

        return () => observer.disconnect();
    }, [list.length]);

    const getErrors = (index) => ({
        ...(errors[index] || {}),
        ...(localErrors[index] || {}),
    });

    const handleAdd = () => {
        onChange([...(items || []), emptyItem()]);
    };

    const handleRemoveClick = (index) => {
        const item = (items || [])[index];
        if (!item) return;
        if (item.deliveryItemId && deliveryId) {
            setConfirmRemoveIndex(index);
        } else {
            const next = (items || []).filter((_, i) => i !== index);
            onChange(next);
        }
    };

    const handleConfirmRemove = async () => {
        if (confirmRemoveIndex == null) return;
        const item = (items || [])[confirmRemoveIndex];
        if (!item?.deliveryItemId || !deliveryId) {
            setConfirmRemoveIndex(null);
            return;
        }
        setRemovingIndex(confirmRemoveIndex);
        try {
            await deliveryService.deleteItem(deliveryId, item.deliveryItemId, customerIdForApi);
            const next = (items || []).filter((_, i) => i !== confirmRemoveIndex);
            onChange(next);
            setConfirmRemoveIndex(null);
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Erro ao remover item da entrega.',
            });
            setConfirmRemoveIndex(null);
        } finally {
            setRemovingIndex(null);
        }
    };

    const handleChange = (index, field, value) => {
        const next = (items || []).map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        );
        onChange(next);
    };

    const handleSave = async (index) => {
        if (!deliveryId) {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message: 'Crie a entrega antes de salvar itens.',
            });
            return;
        }
        const item = (items || [])[index];
        if (!item) return;

        const currentErrors = {};
        const quantity = parseInt(item.quantity, 10);
        if (!Number.isInteger(quantity) || quantity < 1) {
            currentErrors.quantity = 'Informe a quantidade (mínimo 1).';
        }
        if (!item.description || !item.description.trim()) {
            currentErrors.description = 'Descrição é obrigatória.';
        }

        if (Object.keys(currentErrors).length > 0) {
            setLocalErrors((prev) => ({ ...prev, [index]: currentErrors }));
            return;
        }

        setSavingIndex(index);
        try {
            if (item.deliveryItemId) {
                await deliveryService.updateItem(deliveryId, item.deliveryItemId, item, customerIdForApi);
            } else {
                const result = await deliveryService.createItem(deliveryId, item, customerIdForApi);
                if (result && result.id != null) {
                    const next = (items || []).map((it, i) =>
                        i === index ? { ...it, deliveryItemId: result.id } : it
                    );
                    onChange(next);
                }
            }
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Erro ao salvar item da entrega.',
            });
        } finally {
            setSavingIndex(null);
        }
    };

    return (
        <>
            <p style={{ color: '#666', marginBottom: '16px' }}>
                Adicione os itens da entrega. Campos obrigatórios: <strong>Quantidade</strong> e <strong>Descrição</strong>.
            </p>

            <ItemsListScroll ref={listScrollRef} $maxHeight={scrollMaxHeight}>
                {list.map((item, index) => {
                    const fieldErrors = getErrors(index);
                    return (
                        <ItemCard key={item.id} data-delivery-item-card>
                            <SectionTitle style={{ marginTop: 0 }}>
                                Item {index + 1}
                                {item.deliveryItemId && (
                                    <span style={{ color: '#666', fontSize: '0.9em', fontWeight: 'normal' }}> | ID do item: #{item.deliveryItemId}</span>
                                )}
                                <RemoveButton
                                    type="button"
                                    onClick={() => handleRemoveClick(index)}
                                    disabled={removingIndex === index}
                                    style={{ float: 'right', width: 'auto', paddingLeft: '12px' }}
                                >
                                    {removingIndex === index ? 'Removendo...' : 'Remover'}
                                </RemoveButton>
                            </SectionTitle>

                            <FormRow>
                                <FormGroup>
                                    <label>Quantidade *</label>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={item.quantity}
                                        onChange={(e) => handleChange(index, 'quantity', e.target.value)}
                                        $hasError={!!fieldErrors.quantity}
                                        placeholder="Ex: 2"
                                    />
                                    {fieldErrors.quantity && (
                                        <span className="error">{fieldErrors.quantity}</span>
                                    )}
                                </FormGroup>
                                <FormGroup>
                                    <label>Altura (cm)</label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={item.height}
                                        onChange={(e) => handleChange(index, 'height', e.target.value)}
                                        placeholder="Ex: 30"
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <label>Largura (cm)</label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={item.width}
                                        onChange={(e) => handleChange(index, 'width', e.target.value)}
                                        placeholder="Ex: 20"
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <label>Comprimento (cm)</label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={item.length}
                                        onChange={(e) => handleChange(index, 'length', e.target.value)}
                                        placeholder="Ex: 40"
                                    />
                                </FormGroup>
                            </FormRow>

                            <FormRow>
                                <FormGroup>
                                    <label>Peso (kg)</label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={item.weight}
                                        onChange={(e) => handleChange(index, 'weight', e.target.value)}
                                        placeholder="Ex: 5.5"
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <label>Preço (R$)</label>
                                    <Input
                                        type="text"
                                        inputMode="decimal"
                                        value={formatCurrencyBR(item.price)}
                                        onChange={(e) => handleChange(index, 'price', parseCurrencyCentsFromInput(e.target.value))}
                                        placeholder="R$ 0,00"
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <label>Documento (NF)</label>
                                    <Input
                                        type="text"
                                        value={item.documentNumber}
                                        onChange={(e) => handleChange(index, 'documentNumber', e.target.value)}
                                        placeholder="Nº da nota fiscal"
                                    />
                                </FormGroup>
                                <FormGroup style={{ justifyContent: 'center' }}>
                                    <CheckboxGroup>
                                        <input
                                            type="checkbox"
                                            id={`fragile-${item.id}`}
                                            checked={!!item.fragile}
                                            onChange={(e) => handleChange(index, 'fragile', e.target.checked)}
                                        />
                                        <label htmlFor={`fragile-${item.id}`}>Frágil</label>
                                    </CheckboxGroup>
                                </FormGroup>
                            </FormRow>

                            <FormGroup>
                                <label>Descrição *</label>
                                <textarea
                                    rows={2}
                                    value={item.description}
                                    onChange={(e) => handleChange(index, 'description', e.target.value)}
                                    placeholder="Descrição do item"
                                    style={{
                                        minWidth: '100%',
                                        resize: 'vertical',
                                        padding: '10px',
                                        border: `1px solid ${errors[index]?.description ? '#FC5050' : '#BCBCBC'}`,
                                        borderRadius: '4px',
                                        font: 'inherit',
                                    }}
                                />
                                {fieldErrors.description && (
                                    <span className="error">{fieldErrors.description}</span>
                                )}
                            </FormGroup>
                            <FormRow style={{ justifyContent: 'flex-end', marginTop: '8px' }}>
                                <Button
                                    type="button"
                                    variant="primary"
                                    onClick={() => handleSave(index)}
                                    disabled={savingIndex === index}
                                    style={{ justifyContent: 'center' }}
                                >
                                    {savingIndex === index ? 'Salvando...' : item.deliveryItemId ? 'Atualizar' : 'Salvar'}
                                </Button>
                            </FormRow>
                        </ItemCard>
                    );
                })}
            </ItemsListScroll>

            <Button type="button" variant="outline" onClick={handleAdd}>
                + Adicionar item
            </Button>

            {confirmRemoveIndex != null && (
                <ConfirmRemoveItemModal
                    onConfirm={handleConfirmRemove}
                    onClose={() => setConfirmRemoveIndex(null)}
                    isRemoving={removingIndex !== null}
                />
            )}

            {messageModal.open && (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                />
            )}
        </>
    );
}
